import { memo, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import _ from "lodash";

import { AddIcon, RemoveIcon } from "icons";

import Button from "components/Button";
import { CircularProgress } from "components/Loading";
import Stack from "components/Stack";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { nGramSimilarity } from "./utils";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PageMatch {
  pageIndex: number;
  similarity: number;
}

export interface IPDFEmbed {
  pdfFileUrl: string | undefined;
  focusedQuoteClearanceCallback: () => void;
  focusedQuote?: string | null;
}

const PDFEmbed = ({
  pdfFileUrl,
  focusedQuote,
  focusedQuoteClearanceCallback,
}: IPDFEmbed) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1.5);
  const [numPages, setNumPages] = useState(0);
  const [pdfPageToTextContentMapping, setPdfPageToTextContentMapping] =
    useState<{ [key: number]: string }>({});
  const [showLoading, hideLoading] = useLoadingBackdrop();

  useEffect(() => {
    if (_.isNil(focusedQuote)) {
      return;
    }

    const mostSimilarPage: PageMatch = Object.keys(
      pdfPageToTextContentMapping
    ).reduce<PageMatch>(
      (bestMatch, pageIndex) => {
        const pageText = pdfPageToTextContentMapping[parseInt(pageIndex)];
        const similarity = nGramSimilarity({
          fullText: pageText,
          searchString: focusedQuote,
        });

        return similarity > bestMatch.similarity
          ? { pageIndex: parseInt(pageIndex), similarity }
          : bestMatch;
      },
      { pageIndex: -1, similarity: -1 }
    );

    const pageElement = document.querySelector(
      `[data-page-number="${mostSimilarPage.pageIndex}"]`
    );

    if (pageElement === null) {
      toast.errorMessage(
        "We're sorry but we couldn't find the source page you're looking for."
      );
      return;
    }

    pageElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    pageElement.classList.add("highlighted");

    setTimeout(() => {
      pageElement.classList.remove("highlighted");
    }, 1300);

    focusedQuoteClearanceCallback();
  }, [
    pdfPageToTextContentMapping,
    focusedQuote,
    showLoading,
    hideLoading,
    focusedQuoteClearanceCallback,
  ]);

  const handlePageTextLayerLoadSuccess = ({
    pageNumber,
    textContent,
  }: {
    pageNumber: number;
    textContent: { items: Array<any> };
  }) => {
    setPdfPageToTextContentMapping((prevState) => ({
      ...prevState,
      [pageNumber]: textContent.items.map((item) => item.str).join(" "),
    }));
  };

  return (
    <Stack
      sx={{
        alignItems: "center",
        height: "100%",
        position: "relative",
        overflowY: "auto",
        "&:hover .zoomControls": {
          opacity: 1,
        },
        "canvas.react-pdf__Page__canvas": {
          width: "100% !important",
          height: "auto !important",
        },
        ".textLayer": {
          pointerEvents: "none",
          display: "none",
        },
        ".highlighted": {
          animationDelay: "350ms",
          animation: "zoomInAndOut 1s linear",
          "@keyframes zoomInAndOut": {
            "0%": {
              transform: "scale(1)",
            },
            "50%": {
              transform: "scale(1.1)",
            },
            "100%": {
              transform: "scale(1)",
            },
          },
        },
      }}
      ref={containerRef}
    >
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "absolute",
        }}
      >
        <CircularProgress />
      </div>

      {!_.isNil(pdfFileUrl) && (
        <>
          <Stack
            direction="row"
            className="zoomControls"
            sx={{
              top: "10px",
              marginBottom: "-50px",
              position: "sticky",
              justifyContent: "center",
              zIndex: 3,
              opacity: 0,
              width: containerRef.current?.clientWidth,
              transition: "opacity 0.2s",
            }}
          >
            <Button
              size="small"
              title="Zoom out"
              variant="outlined"
              onClick={() => setScale((prevScale) => prevScale - 0.1)}
              sx={{ height: "50px" }}
            >
              <RemoveIcon sx={{ width: "40px", height: "40px" }} />
            </Button>
            <Button
              size="small"
              title="Zoom in"
              variant="outlined"
              onClick={() => setScale((prevScale) => prevScale + 0.1)}
              sx={{ height: "50px" }}
            >
              <AddIcon sx={{ width: "40px", height: "40px" }} />
            </Button>
          </Stack>
          <Document
            file={pdfFileUrl}
            loading={null}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          >
            {numPages > 1 &&
              _.range(1, numPages + 1).map((pageNumber) => (
                <Page
                  loading={null}
                  key={pageNumber}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  scale={scale}
                  onGetTextSuccess={(textContent) =>
                    handlePageTextLayerLoadSuccess({ pageNumber, textContent })
                  }
                />
              ))}
          </Document>
        </>
      )}
    </Stack>
  );
};

export default memo(
  PDFEmbed,
  (prevProps, nextProps) =>
    prevProps.pdfFileUrl === nextProps.pdfFileUrl &&
    prevProps.focusedQuote === nextProps.focusedQuote
);
